<template>
  <div class="border border-base-300 rounded-lg p-4 bg-base-100">
    <div class="flex items-center justify-between" :style="isRtl === true ? { direction: 'rtl' } : { direction: 'ltr' }">
      <div>
        <h3 class="font-semibold text-sm">{{ t('aiChatAgentSetup.rules.libraryTitle', 'Rule Library') }}</h3>
        <div class="text-xs opacity-70 mt-1">{{ t('aiChatAgentSetup.rules.detectedLanguage', 'Detected language') }}: {{ detectedLanguageLabel }}</div>
        <div class="text-xs opacity-70 mt-1">{{ t('aiChatAgentSetup.rules.libraryDescription', 'Open the library to browse recommended rules by language and tags.') }}</div>
      </div>
      <button type="button" class="btn btn-sm btn-outline" @click="openLibraryModal">
        {{ t('aiChatAgentSetup.rules.openLibraryButton', 'Open Library') }}
      </button>
    </div>
    <div class="mt-4 rounded-xl border border-base-300 bg-base-200/50 p-4" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
      <div class="flex items-center justify-between">
        <div class="text-xs font-semibold uppercase tracking-wide opacity-70">{{ t('aiChatAgentSetup.rules.recommendedTitle', 'Recommended rules') }}</div>
        <div class="text-xs opacity-60">{{ t('aiChatAgentSetup.rules.recommendedHint', 'One click to add') }}</div>
      </div>
      <div class="mt-3">
        <div v-for="item in recommendedRules" :key="item.id" class="rounded-lg border border-base-300 bg-base-100 p-3" :class="isRtl === true ? 'mb-3' : 'mb-3'">
          <div class="flex items-center justify-between">
            <div class="min-w-0 flex-1">
              <div class="text-sm font-medium">{{ item.title }}</div>
              <div class="mt-1 text-xs opacity-75 whitespace-pre-wrap">{{ item.rule }}</div>
            </div>
            <button type="button" class="btn btn-sm min-w-20" :class="isRtl === true ? 'mr-4 self-center' : 'ml-4 self-center'" :disabled="existingRuleSet.has(item.rule.trim()) === true" @click="selectRule(item.rule)">
              {{ existingRuleSet.has(item.rule.trim()) === true ? t('aiChatAgentSetup.rules.alreadyAddedButton', 'Added') : t('aiChatAgentSetup.rules.addRecommendedButton', 'Add') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <AppModal
    :visible="showLibraryModal === true"
    :title="t('aiChatAgentSetup.rules.libraryTitle', 'Rule Library')"
    box-class="max-w-6xl w-11/12"
    @close="closeLibraryModal"
  >
    <div class="border border-base-300 rounded-lg p-3 bg-base-100 mb-3">
      <input
        v-model="searchText"
        type="text"
        class="input input-bordered input-sm w-full"
        :placeholder="t('aiChatAgentSetup.rules.librarySearchPlaceholder', 'Search rules')"
        :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }"
      />
      <div class="text-xs opacity-70 mt-2 mb-2" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
        {{ t('aiChatAgentSetup.rules.detectedLanguage', 'Detected language') }}: {{ detectedLanguageLabel }}
      </div>
      <div class="flex flex-wrap items-center mb-2">
        <button type="button" class="btn btn-xs mr-2 mb-2" :class="activeTag === 'all' ? 'btn-neutral' : 'btn-outline'" @click="activeTag = 'all'">{{ t('aiChatAgentSetup.rules.allTags', 'All tags') }}</button>
        <button v-for="tag in availableTags" :key="tag" type="button" class="btn btn-xs mr-2 mb-2" :class="activeTag === tag ? 'btn-neutral' : 'btn-outline'" @click="activeTag = tag">#{{ tag }}</button>
      </div>
      <div class="flex flex-wrap items-center">
        <button type="button" class="btn btn-xs mr-2 mb-2" :class="activeLanguageFilter === 'all' ? 'btn-neutral' : 'btn-outline'" @click="activeLanguageFilter = 'all'">{{ t('aiChatAgentSetup.rules.allLanguages', 'All languages') }}</button>
        <button type="button" class="btn btn-xs mr-2 mb-2" :class="activeLanguageFilter === 'he' ? 'btn-neutral' : 'btn-outline'" @click="activeLanguageFilter = 'he'">Hebrew</button>
        <button type="button" class="btn btn-xs mr-2 mb-2" :class="activeLanguageFilter === 'en' ? 'btn-neutral' : 'btn-outline'" @click="activeLanguageFilter = 'en'">English</button>
        <button type="button" class="btn btn-xs mr-2 mb-2" :class="activeLanguageFilter === 'any' ? 'btn-neutral' : 'btn-outline'" @click="activeLanguageFilter = 'any'">Any</button>
      </div>
    </div>
    <div class="max-h-[60vh] overflow-y-auto space-y-2">
      <div v-for="item in filteredLibrary" :key="item.id" class="border border-base-300 rounded-lg p-3 bg-base-200">
        <div class="flex items-center justify-between">
          <div class="font-semibold text-sm" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ item.title }}</div>
          <button type="button" class="btn btn-xs btn-outline" @click="selectRule(item.rule)">{{ t('aiChatAgentSetup.rules.useRuleButton', 'Use Rule') }}</button>
        </div>
        <div class="mt-2 flex flex-wrap items-center">
          <span class="badge badge-outline badge-sm mr-2 mb-1">{{ item.language }}</span>
          <span v-for="tag in item.tags" :key="item.id + '_modal_' + tag" class="badge badge-ghost badge-sm mr-2 mb-1">#{{ tag }}</span>
        </div>
        <div class="text-xs mt-2 whitespace-pre-wrap" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ item.rule }}</div>
      </div>
      <div v-if="filteredLibrary.length === 0" class="text-xs opacity-70 p-2 border border-base-300 rounded-lg">
        {{ t('aiChatAgentSetup.rules.noRuleLibraryResults', 'No matching rules found') }}
      </div>
    </div>
  </AppModal>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from '@/i18n';
import AppModal from '@/components/AppModal.vue';
import ruleLibrary from '../shared/agentRuleLibrary.json';

const props = defineProps({
  isRtl: {
    type: Boolean,
    default: false
  },
  existingRules: {
    type: Array,
    default: () => []
  },
  selectedLanguage: {
    type: String,
    default: 'auto'
  },
  agentDescription: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['add-rule']);
const { t } = useI18n();
const showLibraryModal = ref(false);
const searchText = ref('');
const activeTag = ref('all');
const activeLanguageFilter = ref('all');

const normalizedLibrary = computed(() => {
  const rows = Array.isArray(ruleLibrary) === true ? ruleLibrary : [];
  return rows.map((item) => ({
    id: typeof item.id === 'string' ? item.id : '',
    title: typeof item.title === 'string' ? item.title : '',
    rule: typeof item.rule === 'string' ? item.rule : '',
    language: typeof item.language === 'string' ? item.language : 'any',
    tags: Array.isArray(item.tags) === true ? item.tags : []
  }));
});

const existingRuleSet = computed(() => {
  const set = new Set();
  const rows = Array.isArray(props.existingRules) === true ? props.existingRules : [];
  for (let i = 0; i < rows.length; i += 1) {
    if (typeof rows[i] === 'string') {
      set.add(rows[i].trim());
    }
  }
  return set;
});

const detectedLanguage = computed(() => {
  const description = typeof props.agentDescription === 'string' ? props.agentDescription : '';
  if (/[\u0590-\u05FF]/.test(description) === true) {
    return 'he';
  }
  if (/[\u0600-\u06FF]/.test(description) === true) {
    return 'ar';
  }
  if (/[a-zA-Z]/.test(description) === true) {
    return 'en';
  }
  const selected = typeof props.selectedLanguage === 'string' ? props.selectedLanguage : 'auto';
  if (selected === 'he' || selected === 'ar' || selected === 'en') {
    return selected;
  }
  return 'en';
});

const detectedLanguageLabel = computed(() => {
  if (detectedLanguage.value === 'he') {
    return 'Hebrew';
  }
  if (detectedLanguage.value === 'ar') {
    return 'Arabic';
  }
  if (detectedLanguage.value === 'en') {
    return 'English';
  }
  return 'Auto';
});

const inferredTags = computed(() => {
  const text = typeof props.agentDescription === 'string' ? props.agentDescription.toLowerCase() : '';
  const tags = [];
  if (text.includes('מגדר') === true || text.includes('gender') === true) {
    tags.push('gender');
  }
  if (text.includes('שפה') === true || text.includes('language') === true) {
    tags.push('language');
  }
  if (text.includes('רפוא') === true || text.includes('medical') === true || text.includes('clinical') === true) {
    tags.push('medical');
  }
  if (text.includes('רגיש') === true || text.includes('sensitive') === true || text.includes('safety') === true) {
    tags.push('safety');
  }
  if (text.includes('קישור') === true || text.includes('לינק') === true || text.includes('link') === true) {
    tags.push('links');
  }
  if (text.includes('הבהר') === true || text.includes('clarif') === true) {
    tags.push('clarification');
  }
  if (text.includes('עקביות') === true || text.includes('context') === true || text.includes('history') === true) {
    tags.push('consistency');
  }
  return tags;
});

const availableTags = computed(() => {
  const tags = new Set();
  const rows = normalizedLibrary.value;
  for (let i = 0; i < rows.length; i += 1) {
    const itemTags = Array.isArray(rows[i].tags) === true ? rows[i].tags : [];
    for (let j = 0; j < itemTags.length; j += 1) {
      tags.add(itemTags[j]);
    }
  }
  return Array.from(tags);
});

const rankedLibrary = computed(() => {
  const rows = normalizedLibrary.value;
  const scored = [];
  for (let i = 0; i < rows.length; i += 1) {
    const item = rows[i];
    let score = 0;
    if (item.language === detectedLanguage.value) {
      score = score + 8;
    } else if (item.language === 'any') {
      score = score + 4;
    }
    for (let j = 0; j < inferredTags.value.length; j += 1) {
      if (item.tags.includes(inferredTags.value[j]) === true) {
        score = score + 3;
      }
    }
    if (existingRuleSet.value.has(item.rule.trim()) === true) {
      score = score - 30;
    }
    scored.push({ item, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.map((row) => row.item);
});

const filteredLibrary = computed(() => {
  const term = typeof searchText.value === 'string' ? searchText.value.trim().toLowerCase() : '';
  const rows = rankedLibrary.value;
  return rows.filter((item) => {
    if (activeLanguageFilter.value !== 'all' && item.language !== activeLanguageFilter.value) {
      return false;
    }
    if (activeTag.value !== 'all' && item.tags.includes(activeTag.value) === false) {
      return false;
    }
    if (term.length === 0) {
      return true;
    }
    const title = item.title.toLowerCase();
    const rule = item.rule.toLowerCase();
    const tags = item.tags.join(' ').toLowerCase();
    return title.includes(term) === true || rule.includes(term) === true || tags.includes(term) === true;
  });
});

const recommendedRules = computed(() => {
  return [
    {
      id: 'recommended-escalation',
      title: t('aiChatAgentSetup.rules.examples.escalationTitle', 'Escalate when needed'),
      rule: t('aiChatAgentSetup.rules.examples.escalationRule', 'If the user asks for billing changes, cancellations, refunds, legal commitments, or account ownership actions, do not guess. Explain the limitation clearly and route the user to a human support agent.')
    },
    {
      id: 'recommended-collect-details',
      title: t('aiChatAgentSetup.rules.examples.collectDetailsTitle', 'Collect the right details'),
      rule: t('aiChatAgentSetup.rules.examples.collectDetailsRule', 'When handling a support problem, first collect the minimum details needed to help: product or service name, issue summary, urgency, and any order, ticket, or phone number only if relevant.')
    },
    {
      id: 'recommended-next-step',
      title: t('aiChatAgentSetup.rules.examples.nextStepTitle', 'Always give a next step'),
      rule: t('aiChatAgentSetup.rules.examples.nextStepRule', 'For every support reply, provide one clear next step the user can take now, and when possible include the exact action, page, or team they should contact.')
    }
  ];
});

function openLibraryModal() {
  showLibraryModal.value = true;
}

function closeLibraryModal() {
  showLibraryModal.value = false;
}

function selectRule(ruleText) {
  if (typeof ruleText !== 'string' || ruleText.trim().length === 0) {
    return;
  }
  emit('add-rule', ruleText);
}
</script>
